import { formatCurrencyAmount, parseCurrency } from '../../../../../utils/library';
import {
  greaterThanZero,
  number,
  required,
  requiredWithoutZero
} from '../../../../../utils/validation';
import renderTextField from '../../../../../components/form-controls/RenderTextField';
import RenderCommonDropdownField from '../../../../../components/form-controls/outlinedDropdown';
import RenderOutlinedTextField from '../../../../../components/material-textField/';
import MaterialUIPickers from '../../../../../components/form-controls/RenderMiDatePicker';
import { r_maxLength } from '../../../../../utils/validation/restrictions';
import { API_URL } from '../../../../../utils/config';
import CheckboxField from '../../../../../components/input/CheckboxField';
import moment from 'moment';
import { handleCheckboxChange } from './methods';
import { CrossSellingFormType } from './types';
import {
  BETTER_FX_ICON,
  CASH_ICON,
  EARTH_ICON,
  EXCHANGE_ICON,
  FILLED_CHECKBOX_ICON,
  HILL_BLUE_ICON,
  MAP_PIN_ICON,
  SHIELD_ICON
} from '@Images';

export const NIUM_FOREX_CROSS_SELL_TEXT = {
  bannerTitle: `Traveling abroad?`,
  bannerDescription: `Buy prepaid forex card or buy and sell forex`,
  forexCardFormTItle: `Prepaid forex card`,
  forexCardFormDescription: `Let’s begin with some necessary information and get you started`,
  forexExploreHelpText: {
    desc1: 'For more information, please reach out to our support team',
    desc2: 'or visit',
    desc3: 'Help Centre',
    email: 'instarem.support@niumforex.com'
  },
  nf_cross_sell_success_view: {
    icon: HILL_BLUE_ICON,
    title: `Your details have been successfully recorded`,
    description: `One of our representatives will contact you within 24 hours to assist with the next step in the process.`,
    buttonText: 'Go to home'
  },
  nf_cross_sell_discovery_banner: {
    icon: BETTER_FX_ICON,
    title: `Traveling abroad?`,
    description: `Buy prepaid forex card or buy and sell forex`
  }
};

export const niumForexCrossSellFields = (
  formValues: CrossSellingFormType,
  props: any,
  crossSellingState: any
) => {
  const {
    explore: { screens },
    currentExploreTab
  } = crossSellingState;
  const NIUM_FOREX_CROSS_SELL_FORM_FIELDS: any = [
    {
      name: 'forex_amount',
      type: 'text',
      required: true,
      format: formatCurrencyAmount,
      validate: [requiredWithoutZero, greaterThanZero],
      parse: parseCurrency,
      disableUnderlineStyling: true,
      component: renderTextField,
      disabled: false,
      maxLength: 12
    },
    {
      placeholder: 'City',
      name: 'city',
      type: 'dropdown',
      component: RenderCommonDropdownField,
      validate: [required],
      required: true,
      disabled: false,
      width: '100%',
      url: `${API_URL}/public/niumForex/cities`,
      bindToThisObject: formValues['city']
    },
    {
      name: 'travel_date',
      type: 'date_picker',
      placeholder: 'Travel Date',
      component: MaterialUIPickers,
      validate: [required],
      required: true,
      disabled: false,
      bindToThisObject: formValues['travel_date'],
      disableUnderlineStyling: true,
      showCalendarIcon: true,
      isBlackIcon: true,
      label: 'Travel date',
      margin: 'none',
      minDate: moment().add(1, 'day')
    },
    {
      placeholder: 'Email',
      name: 'email',
      type: 'text',
      component: RenderOutlinedTextField,
      validate: [required],
      required: true,
      disabled: false,
      parentClassName: 'text-input-field',
      fullWidth: true,
      maxWidth: '450px'
    },
    {
      placeholder: 'Phone number',
      name: 'contact_number',
      type: 'text',
      component: RenderOutlinedTextField,
      validate: [required, number],
      normalize: r_maxLength(10),
      required: true,
      disabled: false,
      fullWidth: true,
      parentClassName: 'text-input-field',
      maxWidth: '450px'
    },
    {
      name: 'is_forex_applied',
      checked: formValues['is_forex_applied'],
      bindToThisObject: formValues['is_forex_applied'],
      component: CheckboxField,
      checkboxTextFontSize: '14',
      checkboxTextLineHeight: '18',
      broderBorder: true,
      customImage: FILLED_CHECKBOX_ICON,
      handleChange: () => {
        handleCheckboxChange(props.dispatch, crossSellingState, formValues);
      },
      width: 24,
      height: 24,
      checkboxContent: screens[currentExploreTab].labels?.checkboxContent
    }
  ];
  return NIUM_FOREX_CROSS_SELL_FORM_FIELDS;
};

export const NIUM_FOREX_CROSS_SELL_PROPOSITION = {
  tabs: [
    { name: 'prepaid_forex_card', title: 'Prepaid forex card' },
    { name: 'buy_sell_forex', title: 'Buy and sell forex' }
  ],
  screens: {
    prepaid_forex_card: {
      key: 'prepaid_forex_card',
      title: 'Prepaid forex card',
      description: 'Buy prepaid forex cards for seamless payments overseas.',
      labels: {
        checkboxContent: "I'm also interested in buy and sell forex"
      },
      cards: [
        {
          key: 'cost_effective',
          icon: EXCHANGE_ICON,
          title: 'Cost-Effective',
          description: 'Locks in exchange rates at the time of loading, avoiding fluctuating rates'
        },
        {
          key: 'security',
          icon: SHIELD_ICON,
          title: 'Security',
          description:
            'Safer than carrying cash, with features like chip protection and PIN security'
        },
        {
          key: 'single_currency',
          icon: CASH_ICON,
          title: 'Single currency support',
          description: 'Avoids additional fees associated with multiple currency conversions.'
        }
      ]
    },
    buy_sell_forex: {
      key: 'buy_sell_forex',
      title: 'Buy and sell forex',
      description: 'Exchange currencies at great rates and low fees',
      labels: {
        checkboxContent: "I'm also interested in getting a prepaid forex card"
      },
      cards: [
        {
          key: 'competitive_rate',
          icon: EXCHANGE_ICON,
          title: 'Competitive rates',
          description: 'Offers favorable rates for currency exchange.'
        },
        {
          key: 'convenience',
          icon: MAP_PIN_ICON,
          title: 'Convenience',
          description: 'Easy online process for buying and selling foreign currency.'
        },
        {
          key: 'wide_network',
          icon: EARTH_ICON,
          title: 'Wide network',
          description: 'Extensive coverage, allowing transactions in multiple countries.'
        }
      ]
    }
  }
};

export const NIUM_FOREX_CROSS_SELL_PRODUCTS = ['prepaid_forex_card', 'buy_sell_forex'];

export const NIUM_FOREX_CROSS_SELL_FORM_NAME = 'crossSellingForm';
